import { Box, Icons, iconSize, palette, Typography, fontStacks } from '@munifocus/ui';
import type { ReactNode } from 'react';

/** Static-label outlined input — a hairline field with a soft 10px radius (no MUI floating label). */
export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: palette.background.paper,
    '& fieldset': {
      borderColor: palette.border.lineStrong,
      transition: 'border-color 200ms ease-out',
    },
    '&:hover fieldset': { borderColor: palette.text.muted },
    '&.Mui-focused fieldset': { borderColor: palette.brand.accent, borderWidth: '1.5px' },
    '&.Mui-error fieldset': { borderColor: palette.semantic.emergency },
  },
  '& .MuiOutlinedInput-input': { padding: '13px 4px', fontSize: 15 },
} as const;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Returns a `common.validation.*` key when invalid, or `undefined` when the email is well-formed. */
export function validateEmail(value: string): string | undefined {
  const v = value.trim();
  if (!v) return 'required';
  if (!EMAIL_RE.test(v)) return 'invalidEmail';
  return undefined;
}

/** Small Manrope label rendered above a field (the design uses static labels, not floating ones). */
export function FieldLabel({ htmlFor, children }: { htmlFor: string; children: ReactNode }) {
  return (
    <Typography
      component="label"
      htmlFor={htmlFor}
      sx={{
        display: 'block',
        mb: 0.75,
        fontFamily: fontStacks.display,
        fontWeight: 600,
        fontSize: 13.5,
        color: palette.text.secondary,
      }}
    >
      {children}
    </Typography>
  );
}

/** Tinted red banner with a warning glyph, used for form-level auth errors. */
export function AuthErrorBanner({ children }: { children: ReactNode }) {
  return (
    <Box
      role="alert"
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        p: 1.75,
        borderRadius: '10px',
        backgroundColor: palette.semantic.emergencyTint,
        border: `1px solid ${palette.semantic.emergencyBorder}`,
      }}
    >
      <Box component="span" sx={{ color: palette.semantic.emergency, display: 'flex', mt: '1px' }}>
        <Icons.AlertTriangle size={iconSize.lg} />
      </Box>
      <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, color: palette.semantic.emergencyInk }}>
        {children}
      </Typography>
    </Box>
  );
}
